import {
  MqttCombination,
  mqttFunctionVariables,
  mqttVisibleFunction
} from '@/shared/DefaultValues'
import { AlarmItem, VariableItem } from '@/template/interface'
import { getFormatMessage } from '@/utils/utils'
import { Select, Space, Table, Tabs } from 'antd'
import React, { useEffect, useState } from 'react'

const { TabPane } = Tabs
const formatMessage = getFormatMessage()

export type TopicType = 'pub' | 'sub'
export type SelectionType = 'checkbox' | 'radio'

export const Variable = (props: {
  topicType: TopicType
  selectionType: SelectionType
  selectFunc?: string
  variables?: VariableItem[]
  alarms?: AlarmItem[]
  combinations?: MqttCombination[]
  onChange?: (items: VariableItem[], combination: MqttCombination) => void
}) => {
  const [selectVariables, setSelectVariables] = useState<VariableItem[]>([])
  const [combination, setCombination] = useState<string>()
  const [activeKey, setActiveKey] = useState<string>()
  const [mqttVariables, setMqttVariables] = useState()
  const [variablesVisible, setVariablesVisible] = useState<boolean>(false)

  const internalColumns = [
    {
      title: formatMessage({
        id: 'app.template.transfer.connection.variable.name'
      }),
      dataIndex: 'name',
      key: 'name'
    },
    {
      title: formatMessage({
        id: 'app.template.transfer.connection.variable.dataType'
      }),
      dataIndex: 'dataType',
      key: 'type',
      width: '20%',
      render: (_, record: any) => {
        return formatMessage({ id: `app.dataType.${record.dataType}` })
      }
    },
    {
      title: formatMessage({
        id: 'app.template.transfer.connection.variable.description'
      }),
      dataIndex: 'description',
      key: 'description'
    }
  ]

  const variableColumns = [
    {
      title: formatMessage({
        id: 'app.template.transfer.connection.variable.name'
      }),
      dataIndex: 'name',
      key: 'variable.name'
    },
    {
      title: formatMessage({
        id: 'app.template.transfer.connection.variable.dataType'
      }),
      dataIndex: 'dataType',
      key: 'variable.dataType',
      render: (_, record: any) => {
        return formatMessage({ id: `app.dataType.${record.dataType}` })
      }
    },
    {
      title: formatMessage({
        id: 'app.template.transfer.connection.variable.privilege'
      }),
      dataIndex: 'privilege',
      key: 'variable.privilege',
      render: (_, record: any) => {
        return formatMessage({ id: `app.privilege.${record.privilege}` })
      }
    },
    {
      title: formatMessage({
        id: 'app.template.transfer.connection.variable.description'
      }),
      dataIndex: 'description',
      key: 'variable.description'
    }
  ]

  const alarmColumns = [
    {
      title: formatMessage({
        id: 'app.template.transfer.connection.alarm.name'
      }),
      dataIndex: 'name',
      key: 'alarm.name'
    },
    {
      title: formatMessage({
        id: 'app.template.transfer.connection.alarm.dataType'
      }),
      dataIndex: 'dataType',
      key: 'alarm.dataType',
      render: (_, record: any) => {
        const variable = props.variables?.find((x) => x.id == record.variableId)
        if (variable) {
          return formatMessage({ id: `app.dataType.${variable.dataType}` })
        }
        return ''
      }
    },
    {
      title: formatMessage({
        id: 'app.template.transfer.connection.alarm.msg'
      }),
      dataIndex: 'msg',
      key: 'alarm.msg'
    }
  ]

  const isAlarm = props.selectFunc == 'PushAlarm' ? true : false
  const columns = isAlarm ? alarmColumns : variableColumns
  const data = isAlarm ? props.alarms : props.variables
  const title = isAlarm
    ? formatMessage({
        id: 'app.template.transfer.connection.variable.item.alarm'
      })
    : formatMessage({
        id: 'app.template.transfer.connection.variable.item'
      })

  const change = (items: VariableItem[], combinationId: string) => {
    if (props.onChange) {
      const selectedCombination =
        props.combinations?.find((x) => x.id == combinationId) || {}
      props.onChange(items, selectedCombination)
    }
  }

  const rowSelection = {
    onChange: (selectedRowKeys: React.Key[], selectedRows: any[]) => {
      if (isAlarm) {
        const selectedAlarms = selectedRows.map((alarm) => {
          const variable = props.variables!.find(
            (x) => x.id == alarm.variableId
          )
          return Object.assign({ ...variable }, alarm)
        })
        setSelectVariables(selectedAlarms)
        change(selectedAlarms, combination!)
      } else {
        setSelectVariables(selectedRows)
        change(selectedRows, combination!)
      }
    }
  }

  const setDefaultCombination = () => {
    if (props.combinations && props.combinations.length > 0) {
      let comb = props.combinations?.find((x) => x.name === 'KV')
      if (!comb) {
        comb = props.combinations[0]
      }
      setCombination(comb.id)
    }
  }

  const handleTabChange = (key: string) => {
    setActiveKey(key)
    if (key === 'variable.attri') {
      setDefaultCombination()
    }
  }

  const handleCombinationChange = (value: string) => {
    setCombination(value)
    change(selectVariables, value)
  }

  useEffect(() => {
    setDefaultCombination()
  }, [])

  useEffect(() => {
    if (props.selectFunc) {
      // 过滤当前功能的内置变量列表(过滤条件：功能名称 和 topic类型)
      const interbalVariables =
        mqttFunctionVariables[props.selectFunc][props.topicType]
      setMqttVariables(interbalVariables)

      // 是否显示监控点或报警列表
      const visibleFunctions = mqttVisibleFunction.includes(
        props.selectFunc,
        props.topicType
      )
      setVariablesVisible(visibleFunctions)

      // 设置默认活动选项卡
      setActiveKey(visibleFunctions ? 'variable.item' : 'variable.attri')
    }
  }, [props.selectFunc])

  return (
    <div>
      <Tabs onChange={handleTabChange} activeKey={activeKey}>
        {variablesVisible && (
          <TabPane tab={title} key='variable.item'>
            <Table
              rowKey='id'
              rowSelection={{
                type: props.selectionType,
                ...rowSelection
              }}
              columns={columns}
              dataSource={data}
            />
          </TabPane>
        )}
        <TabPane
          tab={formatMessage({
            id: 'app.template.transfer.connection.variable.internal'
          })}
          key='variable.attri'
        >
          <Table
            rowSelection={{
              type: props.selectionType,
              ...rowSelection
            }}
            columns={internalColumns}
            dataSource={mqttVariables}
          />
        </TabPane>
      </Tabs>
      <Space>
        {formatMessage({
          id: 'app.template.transfer.connection.variable.combination'
        })}
        <Select
          style={{ minWidth: 100 }}
          value={combination}
          onChange={handleCombinationChange}
          disabled={activeKey === 'variable.attri'}
        >
          {props.combinations?.map((x) => (
            <Select.Option key={x.id} value={x.id || 0}>
              {x.name}
            </Select.Option>
          ))}
        </Select>
      </Space>
    </div>
  )
}
